import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProfileService, UpdateProfileDto, ChangePasswordDto } from '../../services/profile.service';
import { AuthService, UserInfo } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit {
  
  // Formularios
  profileForm: FormGroup;
  passwordForm: FormGroup;
  
  // Estados de loading
  isLoadingProfile = false;
  isLoadingPassword = false;
  
  // Usuario actual
  currentUser: UserInfo | null = null;
  
  // Control de tabs
  activeTab: 'profile' | 'password' = 'profile';
  
  // Control de visibilidad de contraseñas
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  constructor(
    private fb: FormBuilder,
    private profileService: ProfileService,
    private authService: AuthService,
    private toastService: ToastService
  ) {
    this.profileForm = this.createProfileForm();
    this.passwordForm = this.createPasswordForm();
  }

  ngOnInit(): void {
    this.loadCurrentUser();
  }

  // ==================== INICIALIZACIÓN ====================

  private createProfileForm(): FormGroup {
    return this.fb.group({
      firstName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50)
      ]],
      lastName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50)
      ]]
    });
  }

  private createPasswordForm(): FormGroup {
    return this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(100),
        ProfileComponent.passwordStrengthValidator
      ]],
      confirmNewPassword: ['', [Validators.required]]
    }, { 
      validators: this.passwordMatchValidator 
    });
  }

  private loadCurrentUser(): void {
    this.currentUser = this.authService.getCurrentUser();
    if (this.currentUser) {
      this.profileForm.patchValue({
        firstName: this.currentUser.firstName,
        lastName: this.currentUser.lastName
      });
    }
  }

  // ==================== VALIDADORES ====================

  private passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmNewPassword = form.get('confirmNewPassword');

    if (newPassword && confirmNewPassword && newPassword.value !== confirmNewPassword.value) {
      confirmNewPassword.setErrors({ mismatch: true });
      return { mismatch: true };
    }

    if (confirmNewPassword?.errors) {
      delete confirmNewPassword.errors['mismatch'];
      if (Object.keys(confirmNewPassword.errors).length === 0) {
        confirmNewPassword.setErrors(null);
      }
    }

    return null;
  }

  // Static validator for password strength
  static passwordStrengthValidator(control: any) {
    const value = control.value;
    if (!value) {
      return null;
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasNonAlphanumeric = /[^a-zA-Z0-9]/.test(value);
    const isLongEnough = value.length >= 6;

    const errors: any = {};

    if (!hasUpperCase) {
      errors.missingUppercase = true;
    }
    if (!hasNonAlphanumeric) {
      errors.missingNonAlphanumeric = true;
    }
    if (!isLongEnough) {
      errors.minLength = true;
    }

    return Object.keys(errors).length === 0 ? null : errors;
  }

  // ==================== GETTERS PARA FORMULARIOS ====================

  // Profile form getters
  get firstName() { return this.profileForm.get('firstName'); }
  get lastName() { return this.profileForm.get('lastName'); }

  // Password form getters
  get currentPassword() { return this.passwordForm.get('currentPassword'); }
  get newPassword() { return this.passwordForm.get('newPassword'); }
  get confirmNewPassword() { return this.passwordForm.get('confirmNewPassword'); }

  // ==================== MANEJO DE ERRORES ====================

  getFieldError(fieldName: string, formType: 'profile' | 'password'): string | null {
    const form = formType === 'profile' ? this.profileForm : this.passwordForm;
    const field = form.get(fieldName);
    
    if (field && field.invalid && (field.dirty || field.touched)) {
      if (field.errors?.['required']) {
        return this.getRequiredErrorMessage(fieldName);
      }
      if (field.errors?.['minlength']) {
        return `Debe tener al menos ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors?.['maxlength']) {
        return `No debe exceder ${field.errors['maxlength'].requiredLength} caracteres`;
      }
      if (field.errors?.['mismatch']) {
        return 'Las contraseñas no coinciden';
      }
      if (field.errors?.['missingUppercase']) {
        return 'La contraseña debe contener al menos una letra mayúscula (A-Z)';
      }
      if (field.errors?.['missingNonAlphanumeric']) {
        return 'La contraseña debe contener al menos un carácter especial (!@#$%^&*)';
      }
      if (field.errors?.['minLength'] && formType === 'password') {
        return 'La contraseña debe tener al menos 6 caracteres';
      }
    }
    
    return null;
  }

  private getRequiredErrorMessage(fieldName: string): string {
    const messages: { [key: string]: string } = {
      firstName: 'El nombre es requerido',
      lastName: 'El apellido es requerido',
      currentPassword: 'La contraseña actual es requerida',
      newPassword: 'La nueva contraseña es requerida',
      confirmNewPassword: 'La confirmación de contraseña es requerida'
    };
    
    return messages[fieldName] || 'Este campo es requerido';
  }

  hasFieldError(fieldName: string, formType: 'profile' | 'password'): boolean {
    const form = formType === 'profile' ? this.profileForm : this.passwordForm;
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  // ==================== MANEJO DE TABS ====================

  setActiveTab(tab: 'profile' | 'password'): void {
    this.activeTab = tab;
    
    // Limpiar formularios cuando se cambia de tab
    if (tab === 'password') {
      this.passwordForm.reset();
    }
  }

  // ==================== VISIBILIDAD DE CONTRASEÑAS ====================

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm'): void {
    switch (field) {
      case 'current':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  // ==================== SUBMIT HANDLERS ====================

  onSubmitProfile(): void {
    this.profileForm.markAllAsTouched();

    if (this.profileForm.invalid) {
      this.toastService.showToast('error', 'Por favor, corrige los errores en el formulario');
      return;
    }

    const updateProfileDto: UpdateProfileDto = {
      firstName: this.firstName?.value.trim(),
      lastName: this.lastName?.value.trim()
    };

    this.isLoadingProfile = true;

    this.profileService.updateProfile(updateProfileDto).subscribe({
      next: (response) => {
        this.isLoadingProfile = false;
        this.toastService.showToast('success', response.message);
        
        // Actualizar la información del usuario en el AuthService
        this.authService.fetchCurrentUser().subscribe({
          next: (updatedUser) => {
            this.currentUser = updatedUser;
            console.log('Información de usuario actualizada');
          },
          error: (error) => {
            console.warn('No se pudo actualizar la información del usuario:', error);
          }
        });
      },
      error: (error: Error) => {
        this.isLoadingProfile = false;
        this.toastService.showToast('error', error.message);
      }
    });
  }

  onSubmitPassword(): void {
    this.passwordForm.markAllAsTouched();

    if (this.passwordForm.invalid) {
      this.toastService.showToast('error', 'Por favor, corrige los errores en el formulario');
      return;
    }

    const changePasswordDto: ChangePasswordDto = {
      currentPassword: this.currentPassword?.value,
      newPassword: this.newPassword?.value,
      confirmNewPassword: this.confirmNewPassword?.value
    };

    this.isLoadingPassword = true;

    this.profileService.changePassword(changePasswordDto).subscribe({
      next: (response) => {
        this.isLoadingPassword = false;
        this.toastService.showToast('success', response.message);
        this.passwordForm.reset();
        
        // Limpiar estados de visibilidad
        this.showCurrentPassword = false;
        this.showNewPassword = false;
        this.showConfirmPassword = false;
      },
      error: (error: Error) => {
        this.isLoadingPassword = false;
        this.toastService.showToast('error', error.message);
      }
    });
  }

  // ==================== MÉTODOS UTILITARIOS ====================

  resetProfileForm(): void {
    if (this.currentUser) {
      this.profileForm.patchValue({
        firstName: this.currentUser.firstName,
        lastName: this.currentUser.lastName
      });
    }
  }

  resetPasswordForm(): void {
    this.passwordForm.reset();
    this.showCurrentPassword = false;
    this.showNewPassword = false;
    this.showConfirmPassword = false;
  }
}
